import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { Button, CircularProgress, IconButton, Tooltip, Typography } from '@material-ui/core';
import AttachFile from '@material-ui/icons/AttachFile';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import ImageOutlined from '@material-ui/icons/ImageOutlined';
import InsertDriveFileOutlined from '@material-ui/icons/InsertDriveFileOutlined';
import DescriptionOutlined from '@material-ui/icons/DescriptionOutlined';
import PictureAsPdfOutlined from '@material-ui/icons/PictureAsPdfOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';
import CloudUploadOutlined from '@material-ui/icons/CloudUploadOutlined';
import AlertDialog from './AlertDialog';
import AttachmentPreviewDialog from './AttachmentPreviewDialog';
import {
    MAX_FILE_SIZE,
    deleteAttachment,
    detectPreviewKind,
    formatFileSize,
    getCachedMeta,
    uploadAttachment
} from '../services/attachments';
import { attachmentFileId } from './attachmentGate';

const UploadButton = withStyles({
    root: {
        fontFamily: 'Assistant',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        borderRadius: '19px',
        color: '#1274e4',
        backgroundColor: 'white',
        border: '1px solid #1274e4',
        textTransform: 'none',
        padding: '6px 20px',
        alignSelf: 'flex-start',
        '&:hover': {
            backgroundColor: 'rgba(18, 116, 228, 0.06)'
        }
    }
})(Button);

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        margin: '12px 32px 0px 32px'
    },
    hiddenInput: {
        display: 'none'
    },
    fileCard: {
        display: 'flex',
        alignItems: 'center',
        border: '1px solid #D0D0D0',
        borderRadius: '8px',
        padding: '8px 12px',
        backgroundColor: '#FAFAFA'
    },
    fileIcon: {
        color: '#1274e4',
        marginRight: '8px',
        marginLeft: '8px'
    },
    fileInfo: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minWidth: 0
    },
    fileName: {
        fontFamily: 'Assistant',
        fontSize: '16px',
        color: '#35373D',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    },
    fileSize: {
        fontFamily: 'Assistant',
        fontSize: '12px',
        color: '#777777'
    },
    uploadingRow: {
        display: 'flex',
        alignItems: 'center'
    },
    uploadingText: {
        fontFamily: 'Assistant',
        fontSize: '14px',
        color: '#777777',
        marginRight: '10px',
        marginLeft: '10px'
    },
    hint: {
        fontFamily: 'Assistant',
        fontSize: '12px',
        color: '#777777',
        marginTop: '4px'
    },
    error: {
        fontFamily: 'Assistant',
        fontSize: '14px',
        color: 'red',
        marginTop: '4px'
    }
});

const kindIcons = {
    image: ImageOutlined,
    pdf: PictureAsPdfOutlined,
    docx: DescriptionOutlined,
    doc: DescriptionOutlined,
    other: InsertDriveFileOutlined
};

export default function AttachmentField({ question, answer, onAnswerChange, RTL }) {
    const classes = useStyles();
    const inputRef = useRef(null);
    const questionnaireId = useSelector(state => state.questionnaire.id);
    // The answer is an object {value} in-session but an array [{value}] once
    // rehydrated on reload — read the file id out of either shape so a file
    // already saved on a filled questionnaire still shows its card + preview.
    const fileId = attachmentFileId(answer);
    const [meta, setMeta] = useState(() => (fileId ? getCachedMeta(fileId) : null));
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);

    const strings = {
        upload: RTL ? 'העלאת קובץ' : 'Upload file',
        uploading: RTL ? 'מעלה קובץ...' : 'Uploading…',
        sizeHint: RTL ? 'גודל מקסימלי: 10MB' : 'Maximum size: 10 MB',
        tooLarge: RTL ? 'הקובץ גדול מהמותר (עד 10MB)' : 'File exceeds the 10 MB limit',
        uploadFailed: RTL ? 'העלאת הקובץ נכשלה, נסו שוב' : 'Upload failed, please try again',
        deleteFailed: RTL ? 'מחיקת הקובץ נכשלה, נסו שוב' : 'Failed to delete the file, please try again',
        preview: RTL ? 'תצוגה מקדימה' : 'Preview',
        replace: RTL ? 'החלפת הקובץ' : 'Replace file',
        delete: RTL ? 'מחיקת הקובץ' : 'Delete file',
        confirmTitle: RTL ? 'מחיקת קובץ' : 'Delete file',
        confirmMessage: RTL ? 'האם למחוק את הקובץ המצורף?' : 'Are you sure you want to delete the attached file?',
        defaultName: RTL ? 'קובץ מצורף' : 'Attached file'
    };

    useEffect(() => {
        if (!fileId) {
            setMeta(null);
            return;
        }
        const cached = getCachedMeta(fileId);
        // Don't wipe meta we already hold for this file on a cache miss
        // (sessionStorage writes can silently fail — see attachments.js).
        setMeta(prev => cached || (prev && prev.id === fileId ? prev : null));
    }, [fileId]);

    const handleFileSelected = async e => {
        const file = e.target.files[0];
        e.target.value = null;
        if (!file) {
            return;
        }
        setError('');
        if (file.size > MAX_FILE_SIZE) {
            setError(strings.tooLarge);
            return;
        }
        setUploading(true);
        try {
            const uploaded = await uploadAttachment(questionnaireId, file);
            setMeta(uploaded);
            // Repoint the answer at the new file. When replacing, the previous
            // file is left in place (it only persists on the next PATCH) and is
            // cleaned up with the questionnaire by the server's auto-delete job —
            // deleting it here would break the saved answer if the page reloads
            // before navigation.
            onAnswerChange({ question_id: question.id, value: uploaded.id });
        } catch (err) {
            console.log('attachment upload failed', err);
            setError(err.code === 'FILE_TOO_LARGE' ? strings.tooLarge : strings.uploadFailed);
        } finally {
            setUploading(false);
        }
    };

    const handleReplaceClick = () => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    };

    const handleDeleteConfirmed = async () => {
        setConfirmOpen(false);
        setError('');
        setDeleting(true);
        try {
            await deleteAttachment(questionnaireId, fileId);
            setMeta(null);
            onAnswerChange({ question_id: question.id, value: '' });
        } catch (err) {
            console.log('attachment delete failed', err);
            const serverMessage = (err && err.serverMessage) || '';
            // "Delete" always means "remove this file from the form". The server
            // may refuse to delete the actual bytes — because the file is shared
            // with another questionnaire (reused via Contact Data) or because this
            // questionnaire is already finalized — but in every such case the bytes
            // are preserved, so we simply detach the file from THIS form. The
            // seeding guard in WizardQuestionView keeps it from coming back.
            const serverRefusedButFileIsSafe = /does not belong|belongs to|used in (?:other|another)|completed questionnaire|file not found/i.test(serverMessage);
            if (serverRefusedButFileIsSafe) {
                setMeta(null);
                onAnswerChange({ question_id: question.id, value: '' });
            } else {
                // No definitive server response (network / unexpected) — keep the
                // file in place and let the user retry.
                setError(strings.deleteFailed);
            }
        } finally {
            setDeleting(false);
        }
    };

    const kind = detectPreviewKind(meta || {});
    const FileIcon = kindIcons[kind] || InsertDriveFileOutlined;
    const sizeText = meta && meta.size ? formatFileSize(meta.size) : '';

    return (
        <div className={classes.root}>
            <input
                ref={inputRef}
                type="file"
                className={classes.hiddenInput}
                onChange={handleFileSelected}
            />

            {!fileId && !uploading &&
                <>
                    <UploadButton startIcon={<AttachFile />} onClick={() => inputRef.current && inputRef.current.click()}>
                        {strings.upload}
                    </UploadButton>
                    <Typography className={classes.hint}>{strings.sizeHint}</Typography>
                </>
            }

            {uploading &&
                <div className={classes.uploadingRow}>
                    <CircularProgress size={20} />
                    <Typography className={classes.uploadingText}>{strings.uploading}</Typography>
                </div>
            }

            {fileId && !uploading &&
                <div className={classes.fileCard}>
                    <FileIcon className={classes.fileIcon} />
                    <div className={classes.fileInfo}>
                        <Typography className={classes.fileName}>{(meta && meta.filename) || strings.defaultName}</Typography>
                        {sizeText && <Typography className={classes.fileSize}>{sizeText}</Typography>}
                    </div>
                    <Tooltip title={strings.preview}>
                        <span>
                            <IconButton size="small" disabled={deleting} onClick={() => setPreviewOpen(true)} aria-label={strings.preview}>
                                <VisibilityOutlined />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={strings.replace}>
                        <span>
                            <IconButton size="small" disabled={deleting} onClick={handleReplaceClick} aria-label={strings.replace}>
                                <CloudUploadOutlined />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={strings.delete}>
                        <span>
                            <IconButton size="small" disabled={deleting} onClick={() => setConfirmOpen(true)} aria-label={strings.delete}>
                                {deleting ? <CircularProgress size={18} /> : <DeleteOutline />}
                            </IconButton>
                        </span>
                    </Tooltip>
                </div>
            }

            {error && <Typography className={classes.error}>{error}</Typography>}

            <AlertDialog
                open={confirmOpen}
                title={strings.confirmTitle}
                message={strings.confirmMessage}
                onCancel={() => setConfirmOpen(false)}
                onOk={handleDeleteConfirmed}
            />

            <AttachmentPreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                fileId={fileId}
                meta={meta}
                RTL={RTL}
            />
        </div>
    );
}
